import {
  selectGameType,
  selectGoblinsTrapDeathHexKeys,
  selectHexes,
  selectPlayers,
  selectUnits,
  selectWarPhaseStage,
} from '../redux/reducers/sharedStateReducer/selectors';
import {WAR_PHASE_STAGES} from '../constants/phases';
import {GAME_TYPES} from '../constants/game';
import {FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER} from '../configs/playbackSpeed';
import {getReplayPlaybackSpeedMultiplier} from '../utils/replay/replayPlaybackSpeed';
import {resolveUnitMovementPath} from '../utils/movement/resolveUnitMovementPath';

/**
 * Анимация перемещения отряда, которое совершил НЕ этот клиент: ход соперника
 * по сети, ход бота, исполненный на чужом клиенте, и воспроизведение записи
 * матча. Свой ход ведет сама сцена (GameScene -> animateUnitMovement), и там
 * состояние меняется уже ПОСЛЕ полета; здесь наоборот — экшен применен, отряд
 * на поле уже стоит на новой ячейке, поэтому по пути идет его двойник (см.
 * HexGridRenderer.showUnitMovementFlight).
 *
 * Сюда же попадает ход бота, отыгранный этим клиентом: его рендерер уже провел
 * отряд по полю, и второй раз вести не надо — такое перемещение отсеивает сам
 * рендерер по своей отметке (checkIfUnitMovementAlreadyAnimated).
 *
 * Не получилось (нет поля, отряд не нашелся, маршрут не восстановился) —
 * отряд просто остается на целевой ячейке, как было до этой анимации.
 *
 * @param {Object} store
 * @param {Object} hexGridRenderer
 * @param {Object} params
 * @param {string} params.unitId
 * @param {Array} params.prevUnits — отряды ДО применения экшена: в самом
 *   экшене исходной ячейки нет
 * @returns {boolean} пошла ли анимация
 */
export const playUnitMovementAnimation = (store, hexGridRenderer, {unitId, prevUnits} = {}) => {
  if (!hexGridRenderer || typeof hexGridRenderer.showUnitMovementFlight !== 'function') {
    return false;
  }

  // Кадры в скрытой вкладке не идут: двойник завис бы на исходной ячейке (а
  // все зависшие двойники разом пошли бы на возврате), и проигрыватель
  // записи ждал бы каждый до срока. Отряд переставляется мгновенно — как
  // остаются без анимации залп и удары
  if (typeof document !== 'undefined' && document.hidden) {
    return false;
  }

  const state = store.getState();
  if (!checkIfUnitMovementAnimationAllowed(state)) {
    return false;
  }
  // Запасной режим витрины главного меню — живая партия ботов: ее шаг
  // задает ползунок витрины (до x20), и переходы наезжали бы друг на друга.
  // Показ там остается мгновенным; запись на витрине идет по правилу записи
  if (hexGridRenderer.scene?.isDemoMode && selectGameType(state) !== GAME_TYPES.REPLAY) {
    return false;
  }

  const unit = (selectUnits(state) || []).find((item) => item.id === unitId);
  if (!unit) {
    return false;
  }

  const players = selectPlayers(state);
  const path = resolveUnitMovementPath({
    hexGridRenderer,
    hexes: selectHexes(state),
    players,
    prevUnits,
    unitId,
    toHexKey: unit.hexKey,
    isManeuver: selectWarPhaseStage(state) === WAR_PHASE_STAGES.MANEUVER,
    noStopHexKeys: selectGoblinsTrapDeathHexKeys(state),
  });
  if (!path) {
    return false;
  }

  // Обычный переход запись ждет, а полет на крыльях длиннее: он сжимается под ее
  // скорость, и до x5 каждый ход виден целиком
  const timeScale = selectGameType(state) === GAME_TYPES.REPLAY ? 1 / getReplayPlaybackSpeedMultiplier() : 1;

  try {
    return hexGridRenderer.showUnitMovementFlight(unit, path, {players, state, timeScale});
  } catch (error) {
    // Анимация — украшение: ее поломка не должна ронять разбор чужого экшена
    console.warn('[UnitMovement] не удалось показать перемещение отряда', error);
    return false;
  }
};

/**
 * Запись матча (и запись на витрине главного меню) до x5 включительно
 * ведет отряды по полю плавно; быстрее — переставляет мгновенно: это уже
 * перемотка (см. FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER). Переход длится
 * дольше шага записи на x4–x5 — проигрыватель ждет его конца (см.
 * ReplayRunner). Вне записи скорость всегда обычная.
 */
export const checkIfUnitMovementAnimationAllowed = (state) => (
  selectGameType(state) !== GAME_TYPES.REPLAY
  || getReplayPlaybackSpeedMultiplier() <= FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER
);
